import {
  addCallbacks,
  transform,
  useApp,
  useSelectedNumberByVariableKey,
  useSelectedValueAttributeByKey,
  useSelectedValueByVariableKey,
  useVariables,
} from '@k3/core';
import { useEffect, useMemo } from 'react';
import {
  K3Discount,
  useDiscountContext,
  useDiscountPrice,
} from '../store/discountContext';
import { partition } from 'lodash';
import {
  useHeight,
  useLabelAndValue,
  useNumberLabelAndNumber,
} from './useHeight';
import { useIsEcktisch, useShopName } from './useShopName';
import { useTischComponents } from '../components/configurator/tisch/order-callback';
import {
  EINZELTEIL_KONFIGURATOR_APP_CODE,
  isModulmoebelKonfigurator,
  TISCH_KONFIGURATOR_APP_CODE,
} from '../constants';

export type OrderCallbackComponent = {
  component: string;
  number: number;
  props: string;
};

export const useOrderCallback = () => {
  const app = useApp();
  const model = useShopName();

  if (app.code === TISCH_KONFIGURATOR_APP_CODE) {
    const depth = useSelectedNumberByVariableKey('depth');
    const height = useHeight();
    const width = useSelectedNumberByVariableKey('width');
    const dekor = useSelectedValueByVariableKey('dekor');
    const edgeDekor = useSelectedValueByVariableKey('edgeDekor');

    const plateMaterial = parseInt(
      (useSelectedValueAttributeByKey('plateMaterial', 'profiltyp')
        ?.value as string) ?? '0'
    );
    const edgeForm = useLabelAndValue('edgeForm');
    const cornerRadius = useLabelAndValue('cornerRadius');

    const legMaterial = useSelectedValueByVariableKey('legMaterial');
    const legDekor = useSelectedValueByVariableKey('legDekor');
    const gestell = useSelectedValueByVariableKey('gestell');

    const sbl = useNumberLabelAndNumber('schenkelBreiteLinks');
    const sbr = useNumberLabelAndNumber('schenkelBreiteRechts');
    const stl = useNumberLabelAndNumber('schenkelTiefeLinks');
    const str = useNumberLabelAndNumber('schenkelTiefeRechts');
    const isEcktisch = useIsEcktisch();

    const leg =
      model === 'Wangentisch'
        ? undefined
        : model.includes('Standard') && legMaterial?.key === 'wood'
        ? { label: 'Beine', value: legDekor?.label }
        : { label: 'Gestell', value: gestell?.label };
    const components = useTischComponents();

    const discount = useTransformedDiscount();

    useEffect(() => {
      addCallbacks({
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        saveConfiguration: (cfg) => {
          const pdfSummary = {
            model,
            depth: `${depth} cm`,
            height: height.value,
            isEcktisch,
            sbl: sbl.value,
            sbr: sbr.value,
            stl: stl.value,
            str: str.value,
            width: `${width} cm`,
            dekor: dekor?.label,
            edgeDekor: edgeDekor?.label,
            leg,
            plateMaterial: plateMaterial,
            edgeForm: edgeForm.value,
            cornerRadius: cornerRadius.value,
            components: components.map((c) => ({
              ...c,
              number: `${c.number}x`,
            })),
          };
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          return transform({ json: { pdfSummary, discount } })(cfg);
        },
      });
    }, [
      model,
      depth,
      height,
      width,
      dekor,
      edgeDekor,
      legMaterial,
      legDekor,
      gestell,
      leg,
      components,
      discount,
      isEcktisch,
      sbl,
      sbr,
      stl,
      str,
      plateMaterial,
      edgeForm,
      cornerRadius,
    ]);
  } else if (app.code === EINZELTEIL_KONFIGURATOR_APP_CODE) {
    const model =
      useSelectedValueAttributeByKey('model', 'shopName')?.value?.toString() ??
      '';
    const depth = useSelectedNumberByVariableKey('depth');
    const height = useLabelAndValue('height');
    const width = useSelectedNumberByVariableKey('width');

    const plateDecor = useLabelAndValue('plateDecor');
    const edgeDecor = useLabelAndValue('edgeDecor');

    const discount = useTransformedDiscount();

    useEffect(() => {
      addCallbacks({
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        saveConfiguration: (cfg) => {
          const pdfSummary = {
            model: model,
            depth: `${depth} cm`,
            height: height.value,
            width: `${width} cm`,
            plateDecor: plateDecor?.value,
            edgeDecor: edgeDecor?.value,
            components: [],
          };
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          return transform({ json: { pdfSummary, discount } })(cfg);
        },
      });
    }, [model, depth, height, width, plateDecor, edgeDecor, discount]);
  } else if (isModulmoebelKonfigurator(app.code)) {
    const depth = useSelectedNumberByVariableKey('depth');
    const height = useHeight();
    const width = useSelectedNumberByVariableKey('width');
    const model = useLabelAndValue('model');
    const corpusDecor = useLabelAndValue('corpusDecor');
    const frontDecor = useLabelAndValue('frontDecor');
    const griffe = useLabelAndValue('griffe');
    const bestandTeile: { component: string; number: number; props: string }[] =
      [];

    const variables = useVariables();
    variables.forEach((variable) => {
      if (variable.key) {
        const pdfComponentTitle = useSelectedValueAttributeByKey(
          variable.key,
          'pdf_component_title'
        );
        const pdfComponentDescription = useSelectedValueAttributeByKey(
          variable.key,
          'pdf_component_description'
        );
        if (pdfComponentTitle?.value && pdfComponentDescription?.value) {
          bestandTeile.push({
            component: (pdfComponentTitle?.value as string) ?? '',
            number: 1,
            props: (pdfComponentDescription?.value as string) ?? '',
          });
        }
      }
    });

    const discount = useTransformedDiscount();

    useEffect(() => {
      addCallbacks({
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        saveConfiguration: (cfg) => {
          const pdfSummary = {
            model: model?.value,
            depth: `${depth} cm`,
            height: height.value,
            width: `${width} cm`,
            corpusDecor: corpusDecor?.value,
            frontDecor: frontDecor?.value,
            griffe: griffe?.value,
            components: bestandTeile,
          };
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          return transform({ json: { pdfSummary, discount } })(cfg);
        },
      });
    }, [
      model,
      depth,
      height,
      width,
      corpusDecor,
      frontDecor,
      bestandTeile,
      griffe,
      discount,
    ]);
  }
};

const defaultDiscountValue = { discountValue: 0 };

const useTransformedDiscount = (): K3Discount | null => {
  const { discount } = useDiscountContext();
  const { discountValue } = useDiscountPrice() ?? defaultDiscountValue;

  return useMemo(() => {
    if (!discount) return null;
    const [[cDiscount], otherDiscounts] = partition(
      discount.discountInformation.discounts,
      (d) => d.discountType === 'C'
    );
    if (!cDiscount) return discount;
    const newCDiscount = { ...cDiscount, discountValue };
    return transform<K3Discount>({
      discountInformation: { discounts: [...otherDiscounts, newCDiscount] },
    })(discount);
  }, [discount, discountValue]);
};
